import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useTranslation, translate } from "@/lib/i18n";
import {
  readConsent,
  writeConsent,
  acceptAll,
  necessaryOnly,
  subscribe,
  VISIBLE_CATEGORIES,
  CONSENT_DEFAULTS,
} from "@/lib/consent";

/**
 * Consent Foundation — granular settings dialog.
 *
 * Rules (spec sections 5, 7, 11):
 *   * Necessary is always shown, always on, with a locked/read-only
 *     toggle marked “always active”.
 *   * Every optional category is off by default and is user-controlled.
 *   * Save persists the exact selection.  Accept-all turns every
 *     currently visible optional category on.  Necessary-only turns
 *     every optional category off.  Cancel closes the dialog without
 *     mutating any consent state — an undecided user stays undecided.
 *   * Withdrawal (turning Analytics from on → off and pressing Save)
 *     runs immediately through the centralized service lifecycle.
 *   * Dialog is a real ARIA dialog (via shadcn `Dialog`) with focus
 *     containment, Escape=Cancel, and focus returned to the trigger.
 */
export default function CookieSettingsDialog({ open, onOpenChange, language = null }) {
  const { t: tPref, locale: prefLocale } = useTranslation();
  // Resolve the effective language for THIS dialog invocation.
  //   * explicit `language` prop wins (route-forced by ConsentProvider),
  //   * else fall back to the persisted app-wide locale.
  // This is derived on every render so a re-open with a different
  // route-forced language switches the whole dialog live without any
  // hard refresh.
  const effectiveLocale =
    language === "en" || language === "de" ? language : prefLocale;
  // Bind `t` to the effective locale so every label — heading,
  // description, current-state, categories, badges, buttons — reads
  // from the same authoritative language, regardless of the reactive
  // persisted locale.  Falls back to the reactive `tPref` when a key
  // is not localized.
  const t = useCallback(
    (key, vars) => {
      if (language === "en" || language === "de") {
        return translate(key, vars, language);
      }
      return tPref(key, vars);
    },
    [language, tPref],
  );
  const locale = effectiveLocale;
  const stored = readConsent();
  const initialSelection = useMemo(() => {
    const src = stored ? stored.categories : CONSENT_DEFAULTS;
    const out = {};
    for (const c of VISIBLE_CATEGORIES) out[c] = !!src[c];
    out.necessary = true; // pinned
    return out;
  }, [stored]);

  const [selection, setSelection] = useState(initialSelection);

  // If the dialog is (re-)opened, snap back to the currently stored
  // state so a previous unsaved fiddle does not persist.
  useEffect(() => {
    if (open) {
      const cur = readConsent();
      const src = cur ? cur.categories : CONSENT_DEFAULTS;
      const out = {};
      for (const c of VISIBLE_CATEGORIES) out[c] = !!src[c];
      out.necessary = true;
      setSelection(out);
    }
  }, [open]);

  // Live-update when consent changes externally (banner Accept all
  // etc.) while the dialog is open, so a subsequent Cancel does not
  // silently revert a fresh acceptance.
  useEffect(() => {
    const unsub = subscribe(() => {
      if (!open) return;
      const cur = readConsent();
      const src = cur ? cur.categories : CONSENT_DEFAULTS;
      const out = {};
      for (const c of VISIBLE_CATEGORIES) out[c] = !!src[c];
      out.necessary = true;
      setSelection(out);
    });
    return unsub;
  }, [open]);

  const toggle = (name) => {
    if (name === "necessary") return; // locked
    setSelection((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSave = () => {
    writeConsent(selection, { locale });
    onOpenChange?.(false);
  };

  const handleAcceptAll = () => {
    acceptAll({ locale });
    onOpenChange?.(false);
  };

  const handleNecessaryOnly = () => {
    necessaryOnly({ locale });
    onOpenChange?.(false);
  };

  const handleCancel = () => {
    onOpenChange?.(false);
  };

  const currentState = stored
    ? stored.categories.analytics
      ? t("consent.dialog.state_all")
      : t("consent.dialog.state_necessary_only")
    : t("consent.dialog.state_none");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Final Browser-Reality Correction Round (2026-08-03).  The
        // shared shadcn `DialogContent` uses `grid gap-4 p-6`, which
        // prevented an inner flex-column + scroll region from taking
        // effect on short viewports (laptop-reduced-height, mobile,
        // 200% zoom).  We break out of the grid and clamp height via
        // inline style (Tailwind arbitrary max-h values were not
        // being applied at runtime — the JIT-compiled bundle only
        // ships classes it saw at build time).  Both `dvh` and `vh`
        // fallbacks are declared so browsers without `dvh` still get
        // a viewport-bounded height.
        className="!grid-cols-1 p-0 max-w-none overflow-hidden w-[min(calc(100vw-2rem),32rem)]"
        style={{
          display: "flex",
          flexDirection: "column",
          // 100dvh accounts for iOS Safari's collapsible URL bar;
          // 90vh is the ultimate fallback so any older browser still
          // sees a viewport-bounded dialog.
          maxHeight: "min(90vh, calc(100dvh - 2rem))",
        }}
        data-testid="cookie-settings-dialog"
      >
        <div
          className="overflow-y-auto px-6 pt-6"
          data-testid="cookie-settings-dialog-scroll-region"
          style={{
            flex: "1 1 auto",
            minHeight: 0,
            paddingBottom: "0.5rem",
          }}
        >
        <DialogHeader>
          <DialogTitle data-testid="cookie-settings-dialog-title">
            {t("consent.dialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("consent.dialog.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <div
          className="mt-2 rounded-md border border-border/60 px-3 py-2 text-xs text-muted-foreground"
          data-testid="cookie-settings-dialog-current-state"
        >
          <span className="font-medium text-foreground">
            {t("consent.dialog.current_state")}:
          </span>{" "}
          {currentState}
          {stored?.updatedAt ? (
            <span className="ml-2 text-muted-foreground/70">
              · {t("consent.dialog.updated_at")}:{" "}
              {formatIso(stored.updatedAt)}
            </span>
          ) : null}
        </div>

        <ul className="mt-4 divide-y divide-border/60">
          <li className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-foreground">
                  {t("consent.dialog.category_necessary_title")}
                </p>
                <span
                  className="rounded-full border border-border/70 px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
                  data-testid="cookie-settings-necessary-locked-label"
                >
                  {t("consent.dialog.category_necessary_locked")}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("consent.dialog.category_necessary_body")}
              </p>
            </div>
            <Switch
              checked={true}
              disabled
              aria-readonly="true"
              aria-label={t("consent.dialog.category_necessary_title")}
              data-testid="cookie-settings-toggle-necessary"
            />
          </li>
          <li className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                {t("consent.dialog.category_analytics_title")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("consent.dialog.category_analytics_body")}
              </p>
            </div>
            <Switch
              checked={!!selection.analytics}
              onCheckedChange={() => toggle("analytics")}
              aria-label={t("consent.dialog.category_analytics_title")}
              data-testid="cookie-settings-toggle-analytics"
            />
          </li>
        </ul>
        </div>{/* /scroll-region */}

        <DialogFooter
          className="border-t border-border/60 bg-background/95 backdrop-blur px-6 py-3 flex flex-wrap gap-2 justify-end shrink-0"
          data-testid="cookie-settings-dialog-actions"
          style={{
            paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)",
          }}
        >
          <Button
            type="button"
            variant="ghost"
            onClick={handleCancel}
            data-testid="cookie-settings-cancel"
          >
            {t("consent.dialog.cancel")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleNecessaryOnly}
            data-testid="cookie-settings-necessary-only"
          >
            {t("consent.dialog.necessary_only")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleAcceptAll}
            data-testid="cookie-settings-accept-all"
          >
            {t("consent.dialog.accept_all")}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            data-testid="cookie-settings-save"
          >
            {t("consent.dialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatIso(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
  } catch {
    return iso;
  }
}
